/**
 * /tools/virtue-affirmations?clientId=X — NL-032 (was STP-030).
 *
 * Lists every active virtue from the client's Carpe Diem catalog in two
 * sections: those currently in the active matrix (`inMatrix`) and those
 * sitting in the Pool ready to be promoted (`inPool`). One editable
 * affirmation textarea per virtue; on-blur saves write through the
 * existing `PUT /clients/:id/carpe-diem/virtues/:vid/affirmation` route.
 *
 * The final "Save & finalize" button writes a `tool_completions` row so
 * the journey-popup completion counts (NL-013/17/21) see this tool as
 * done. Existing schema shape preserved (affirmations[] + activatedAtStep).
 *
 * Empty-state: when both groups are empty, points the user at the
 * Structured Eulogy / Scrambled Virtues tools that feed the Pool.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { ApiError } from '@/lib/api-client';
import type { AffirmationGroupEntry } from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';
import { AxisIcon } from '@/components/axis-icon';

type EditState = Record<string, string>;

// #8 (2026-06-15) — affirmations run per category in the canonical
// Energy → Work → Love order, each headed by its axis icon.
const AFF_AXES = ['energy', 'work', 'love'] as const;
type AffAxis = (typeof AFF_AXES)[number];

export function VirtueAffirmationsPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken, user } = useAuth();
  const clientId = useClientId();

  const [groups, setGroups] = useState<api.AffirmationsByGroup | null>(null);
  const [edits, setEdits] = useState<EditState>({});
  const [savingVirtueId, setSavingVirtueId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  // #2 (batch #4) — snapshot of the affirmations as last finalized, so the
  // Save button can show a disabled "✓ Saved" state until the client edits
  // again (matches the saved-clean pattern other tool forms use).
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const data = await api.getAffirmationsByGroup(accessToken, clientId);
        if (!active) return;
        setGroups(data);
        // Seed edits from server data so the textarea is hydrated.
        const seed: EditState = {};
        for (const v of [...data.inMatrix, ...data.inPool]) {
          seed[v.virtueId] = v.affirmationText ?? '';
        }
        setEdits(seed);
      } catch {
        if (active) setGroups({ inMatrix: [], inPool: [] });
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const totalCount = useMemo(
    () => (groups ? groups.inMatrix.length + groups.inPool.length : 0),
    [groups],
  );

  const filledCount = useMemo(
    () => Object.values(edits).filter((v) => v.trim().length > 0).length,
    [edits],
  );

  // The finalize payload, serialized — compared against `savedSnapshot` to
  // know whether the form is clean (saved) or has unsaved edits.
  const currentSnapshot = useMemo(() => {
    const allVirtues = groups ? [...groups.inMatrix, ...groups.inPool] : [];
    return JSON.stringify(
      allVirtues
        .map((v) => ({ virtueName: v.virtueName, affirmationText: (edits[v.virtueId] ?? '').trim() }))
        .filter((row) => row.affirmationText.length > 0),
    );
  }, [groups, edits]);

  // True once a finalize succeeded and nothing has changed since.
  const isSavedClean = savedAt !== null && savedSnapshot === currentSnapshot;

  // #8 — group every virtue (matrix + pool) by its axis so the tool runs
  // Energy → Work → Love, one compact affirmation row each.
  const byAxis = useMemo(() => {
    const m: Record<AffAxis, AffirmationGroupEntry[]> = {
      energy: [],
      work: [],
      love: [],
    };
    if (groups) {
      for (const v of [...groups.inMatrix, ...groups.inPool]) {
        if (v.domain === 'energy' || v.domain === 'work' || v.domain === 'love') {
          m[v.domain].push(v);
        }
      }
    }
    return m;
  }, [groups]);

  // Save & finalize is enabled once the client has typed at least one
  // affirmation across all virtues (matches the existing rule).
  const canSubmit = !submitting && hydrated && filledCount >= 1;

  const updateEdit = (virtueId: string, value: string): void => {
    setEdits((prev) => ({ ...prev, [virtueId]: value }));
  };

  const persistEdit = async (virtueId: string): Promise<void> => {
    if (!clientId) return;
    const text = (edits[virtueId] ?? '').trim();
    setSavingVirtueId(virtueId);
    try {
      await api.setCarpeDiemAffirmation(accessToken, clientId, virtueId, text);
    } catch {
      // Soft-fail: blur-save errors don't block the user. The "Save &
      // finalize" button below catches a full-page save error if needed.
    } finally {
      setSavingVirtueId(null);
    }
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!user) {
      setSubmitError(t('tools.virtueAffirmations.errors.notAuthenticated'));
      return;
    }
    if (!clientId) {
      setSubmitError(t('tools.virtueAffirmations.errors.missingClient'));
      return;
    }
    if (filledCount === 0) {
      setSubmitError(t('tools.virtueAffirmations.errors.noAffirmations'));
      return;
    }
    setSubmitting(true);
    const allVirtues: AffirmationGroupEntry[] = groups
      ? [...groups.inMatrix, ...groups.inPool]
      : [];
    // Preserve the existing tool_completions payload shape so the
    // journey-popup count keeps working without a server-side change.
    const payload = {
      affirmations: allVirtues
        .map((v) => ({
          virtueName: v.virtueName,
          affirmationText: (edits[v.virtueId] ?? '').trim(),
        }))
        .filter((row) => row.affirmationText.length > 0),
      activatedAtStep: 2 as const,
    };
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'virtue-affirmations',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
      // Mark the form clean so the Save button flips to a disabled "✓ Saved".
      setSavedSnapshot(JSON.stringify(payload.affirmations));
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.virtueAffirmations.errors.generic'));
      } else {
        setSubmitError(t('tools.virtueAffirmations.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section
        className="max-w-2xl mx-auto px-s5 py-s8"
        data-testid="virtue-affirmations-missing-client"
      >
        <h1 className="font-display text-display-lg text-ink">
          {t('tools.virtueAffirmations.title')}
        </h1>
        <p className="mt-s3 text-flame-ember">
          {t('tools.virtueAffirmations.errors.missingClient')}
        </p>
      </section>
    );
  }

  if (hydrated && totalCount === 0) {
    return (
      <section
        className="max-w-3xl mx-auto px-s5 py-s7"
        data-testid="virtue-affirmations-needs-virtues"
      >
        <header className="mb-s6">
          <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
            {t('tools.virtueAffirmations.eyebrow')}
          </p>
          <h1 className="font-display text-display-xl text-ink mt-s2">
            {t('tools.virtueAffirmations.title')}
          </h1>
        </header>
        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <h2 className="font-display text-display-sm text-ink">
            {t('tools.virtueAffirmations.needsVirtues.title')}
          </h2>
          <p className="mt-s2 text-gray-2">
            {t('tools.virtueAffirmations.needsVirtues.body')}
          </p>
          <Link
            to={`/tools/structured-eulogy?clientId=${encodeURIComponent(clientId)}`}
            className="btn-primary inline-flex mt-s4"
            data-testid="virtue-affirmations-needs-virtues-cta"
          >
            {t('tools.virtueAffirmations.needsVirtues.ctaLabel')}
          </Link>
        </article>
      </section>
    );
  }

  // #8 — one compact row per virtue: the virtue name on the left, a single
  // affirmation line on the right. ~half the height of the old per-virtue
  // card (which stacked label + heading + axis + label + a 3-row textarea).
  const renderAffirmationRow = (v: AffirmationGroupEntry): JSX.Element => {
    const text = edits[v.virtueId] ?? '';
    const isSaving = savingVirtueId === v.virtueId;
    return (
      <div
        key={v.virtueId}
        className="flex items-start gap-s3 rounded-md border border-bone-3 bg-bone-2 px-s3 py-s2"
        data-testid={`virtue-affirmation-row-${v.virtueId}`}
      >
        <label
          htmlFor={`virtue-affirmation-${v.virtueId}`}
          className="w-28 shrink-0 pt-[7px] text-sm font-semibold text-ink truncate"
          title={v.virtueName}
        >
          {v.virtueName}
        </label>
        <div className="flex-1">
          <textarea
            id={`virtue-affirmation-${v.virtueId}`}
            value={text}
            onChange={(event) => updateEdit(v.virtueId, event.target.value)}
            onBlur={() => {
              void persistEdit(v.virtueId);
            }}
            placeholder={t('tools.virtueAffirmations.fields.affirmationPlaceholder')}
            className="input-field min-h-[2.5rem] w-full text-sm italic"
            data-testid={`virtue-affirmation-text-${v.virtueId}`}
            rows={1}
          />
          {isSaving && (
            <p
              role="status"
              className="text-[11px] text-gray-2 mt-[2px]"
              data-testid={`virtue-affirmation-saving-${v.virtueId}`}
            >
              {t('tools.virtueAffirmations.saving')}
            </p>
          )}
        </div>
      </div>
    );
  };

  // #8 — per-axis sections, Energy → Work → Love, each headed by the
  // congruent axis icon (bolt / briefcase / heart).
  const axisSections = AFF_AXES.map((axis) => {
    const entries = byAxis[axis];
    if (entries.length === 0) return null;
    return (
      <section
        key={axis}
        className="space-y-s2"
        data-testid={`virtue-affirmations-axis-${axis}`}
      >
        <h2 className="flex items-center gap-s2 font-display text-display-sm text-ink">
          <AxisIcon axis={axis} size={18} />
          {t(`common.axis.${axis}`)}
        </h2>
        <div className="space-y-s2">{entries.map((v) => renderAffirmationRow(v))}</div>
      </section>
    );
  });

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.virtueAffirmations.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.virtueAffirmations.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">
          {t('tools.virtueAffirmations.description')}
        </p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        {axisSections}

        {submitError && (
          <p
            role="alert"
            className="form-error"
            data-testid="virtue-affirmations-submit-error"
          >
            {submitError}
          </p>
        )}

        {savedAt && (
          <p
            role="status"
            className="text-sm text-flame-deep"
            data-testid="virtue-affirmations-saved-at"
          >
            ✓ {t('tools.virtueAffirmations.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
          </p>
        )}

        <FinalizeWarning />

        <button
          type="submit"
          className="btn-primary w-full"
          disabled={!canSubmit || isSavedClean}
          data-testid="virtue-affirmations-submit"
        >
          {submitting
            ? t('tools.virtueAffirmations.submitting')
            : isSavedClean
              ? t('tools.virtueAffirmations.savedButton')
              : t('tools.virtueAffirmations.submit')}
        </button>
      </form>
    </section>
  );
}
